import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar          from "./Sidebar";
import Header           from "./Header";
import Dashboard        from "./Dashboard";
import Depoimentos      from "./Depoimentos";
import Comparativo      from "./Comparativo";
import PDSA             from "./PDSA";
import PDSAIntelligente from "./PDSAIntelligente";
import Rankings         from "./Rankings";
import Tips             from "./Tips";
import Benchmarking     from "./Benchmarking";
import Login            from "./Login";
import Admin            from "./Admin";
import CRM              from "./CRM";
import Configuracoes    from "./Configuracoes";
import { T } from "./theme";

import { Construction, Lock } from "lucide-react";

if (!document.getElementById("truenps-fonts")) {
  const link = Object.assign(document.createElement("link"), {
    id:"truenps-fonts", rel:"stylesheet",
    href:"https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=IBM+Plex+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap",
  });
  document.head.appendChild(link);
}

const globalStyle = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; background: ${T.bgBase}; font-family: ${T.fontBody}; color: ${T.textHi}; -webkit-font-smoothing: antialiased; }
  ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: #9CA3AF; }
  select option { background: #fff; color: #111827; }
  @keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
  @keyframes fadeSlideIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pageEnter { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulseGlow { 0%,100%{opacity:0.4;transform:scale(0.95)} 50%{opacity:1;transform:scale(1.05)} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
`;

function PlaceholderPage({ title }) {
  return (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,padding:24}}>
      <div style={{width:56,height:56,borderRadius:16,background:T.accentDim,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Construction size={24} color={T.accent}/>
      </div>
      <h2 style={{fontFamily:T.fontTitle,fontWeight:700,fontSize:20,color:T.textHi}}>{title}</h2>
      <p style={{fontSize:13,color:T.textLow,textAlign:"center",maxWidth:320}}>Em desenvolvimento.</p>
    </div>
  );
}

function SemPermissao() {
  return (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,padding:24}}>
      <div style={{width:56,height:56,borderRadius:16,background:"rgba(239,68,68,0.10)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Lock size={24} color={T.red}/>
      </div>
      <h2 style={{fontFamily:T.fontTitle,fontWeight:700,fontSize:20,color:T.textHi}}>Sem Permissao</h2>
      <p style={{fontSize:13,color:T.textLow,textAlign:"center",maxWidth:320}}>Voce nao tem acesso a esta area.</p>
    </div>
  );
}

function PDSAWithState() {
  const loc = useLocation();
  return <PDSA cicloNovo={loc.state?.cicloNovo} />;
}

function AnimatedRoutes({ session, setSession }) {
  const location = useLocation();

  const handleLogout = async () => {
    await fetch("/api/logout", { method: "POST", credentials: "include" });
    setSession(null);
  };

  const role = session?.usuario?.role || "viewer";

  return (
    <div style={{display:"flex",height:"100vh",overflow:"hidden"}}>
      <Sidebar session={session} onLogout={handleLogout}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
        <Header session={session} onLogout={handleLogout}/>
        <main style={{flex:1,overflowY:"auto",background:T.bgBase}}>
          <Routes key={location.pathname}>
            <Route path="/"              element={<Dashboard/>}/>
            <Route path="/depoimentos"   element={<Depoimentos/>}/>
            <Route path="/comparativo"   element={<Comparativo/>}/>
            <Route path="/rankings"      element={<Rankings/>}/>
            <Route path="/tips"          element={<Tips/>}/>
            <Route path="/pdsa"          element={<PDSAWithState/>}/>
            <Route path="/qualidade"     element={<PDSAIntelligente/>}/>
            <Route path="/benchmarking"  element={<Benchmarking/>}/>
            <Route path="/configuracoes" element={<Configuracoes/>}/>
            <Route path="/admin"         element={role === "admin" ? <Admin/> : <SemPermissao/>}/>
            <Route path="/crm"           element={["admin","cxo"].includes(role) ? <CRM/> : <SemPermissao/>}/>
            <Route path="*"              element={<Navigate to="/" replace/>}/>
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const style = Object.assign(document.createElement("style"), {id:"truenps-global", textContent: globalStyle});
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch {} };
  }, []);

  useEffect(() => {
    fetch("/api/session", { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        if (d.logged && d.session) {
          const s = d.session;
          setSession(s.usuario ? s : { ...s, usuario: s });
        } else {
          return fetch("/api/auto-login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({}),
          })
          .then(r => r.json())
          .then(d2 => {
            if (d2.success && d2.session) {
              const s2 = d2.session;
              setSession(s2.usuario ? s2 : { ...s2, usuario: s2 });
            }
          });
        }
      })
      .catch(e => console.warn("Session check failed:", e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg,flexDirection:"column",gap:16}}>
      <div style={{width:40,height:40,borderRadius:12,background:T.gradientAccent,display:"flex",alignItems:"center",justifyContent:"center",animation:"spin 0.8s linear infinite"}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
      </div>
      <p style={{fontSize:13,color:T.textLow,fontWeight:500,fontFamily:T.fontBody}}>Carregando TrueNPS...</p>
    </div>
  );

  if (!session) {
    const handleLogin = (sessObj) => {
      if (sessObj && sessObj.usuario) { setSession(sessObj); return; }
      setSession({ ...sessObj, usuario: sessObj });
    };
    return <Login onLogin={handleLogin}/>;
  }

  return <BrowserRouter><AnimatedRoutes session={session} setSession={setSession}/></BrowserRouter>;
}
